import classNames from "classnames";
import React from "react";
import Modal from "react-modal";
import { IUpsellModalProps } from "tsi-common-react/src/apps/configurator/models";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { IProductID } from "tsi-common-react/src/models/nominals";
import { trackUpsellModalAddToCartEvent } from "tsi-common-react/src/utils/analytics";
import { addProductToBasket } from "tsi-common-react/src/utils/basket";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero } from "tsi-common-react/src/utils/money";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

interface IUpsellModalConfig {
    content: {
        imgUrl: string;
        title: string;
        description: string;
        productPageUrl: string;
        topline: string;
        headline: string;
        subline: string;
        overridePrice: number | null;
        valueProps: string[];
        rejectline: string;
    };
    productPid: IProductID;
}

const handleAddToBasket = async (
    event: React.MouseEvent<HTMLElement>,
    product: IProduct,
    setBuyIsDisabled: (disabled: boolean) => void,
    onProceed: () => void,
) => {
    event.preventDefault();
    setBuyIsDisabled(true);
    try {
        await addProductToBasket(product.url);
    } catch (e) {
        setBuyIsDisabled(false);
        return;
    }
    trackUpsellModalAddToCartEvent(product);
    onProceed();
};

export const UpsellModal = (props: IUpsellModalProps): React.ReactNode => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [buyIsDisabled, setBuyIsDisabled] = React.useState(false);

    const openModal = (event?: React.MouseEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }
        const document = getDocument();
        document?.body.setAttribute("style", "overflow-y: hidden");
        setModalIsOpen(true);
    };

    const closeModal = (
        event?: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        const document = getDocument();
        document?.body.setAttribute("style", "overflow: initial");
        setModalIsOpen(false);
        props.onProceed();
    };

    React.useImperativeHandle(
        props.ref,
        () => ({
            openModal,
        }),
        [],
    );

    // TODO Fix this hack. Data should get passed in as a prop.
    // Load the CMS data
    const document = getDocument();
    const upsellConfig = document?.querySelector<HTMLElement>(
        '[data-place-react="modular-configurator"][data-upsell-modal]',
    );
    const cmsData = JSON.parse(
        upsellConfig?.dataset?.upsellModal || "{}",
    ) as IUpsellModalConfig | null;
    if (!cmsData) {
        if (modalIsOpen) {
            props.onProceed();
        }
        return null;
    }
    // Find the correct bundle for this modal
    const bundle = props.bundles.find((b) => {
        const suggestedProductParentIDs = b.suggested_products.map(
            (product) => product.parent?.id || product.id,
        );
        return suggestedProductParentIDs.includes(cmsData.productPid);
    });

    if (!bundle) {
        if (modalIsOpen) {
            props.onProceed();
        }
        return null;
    }
    // Find the product to advertise
    const suggestedProduct = bundle.suggested_products.find(
        (product) => cmsData.productPid === (product.parent?.id || product.id),
    );
    if (
        !suggestedProduct ||
        !suggestedProduct.availability.is_available_to_buy
    ) {
        if (modalIsOpen) {
            props.onProceed();
        }
        return null;
    }
    // Build the modal
    const modalStyle: Modal.Styles = {
        overlay: {
            position: "fixed",
            backgroundColor: "rgba(0, 0, 0, 0.64)",
            zIndex: 2000,
        },
        content: {
            padding: "0",
            overflow: "hidden",
            boxShadow: "none",
        },
    };
    const window = getWindow();
    const windowWidth = window?.innerWidth ?? 1024;
    if (windowWidth < 500) {
        modalStyle.content!.overflow = "scroll";
    }
    const classes = classNames({
        "upsell-modal": windowWidth >= 500,
        "upsell-modal-phone": windowWidth < 500,
    });
    const discountedPrice = cmsData.content.overridePrice
        ? getDinero(suggestedProduct.price.excl_tax).subtract(
              getDinero(cmsData.content.overridePrice),
          )
        : null;
    return (
        <Modal
            // TODO(i18n): Use t tagged template for accessibility label: contentLabel={t`You might also like this product.`}
            contentLabel="You might also like this product."
            className={classes}
            style={modalStyle}
            isOpen={modalIsOpen}
            onRequestClose={(e) => {
                closeModal(e);
            }}
        >
            <div
                className={classNames(
                    styles.upsellModalClose,
                    "al-upsell-modal__close-button",
                )}
                onClick={(e) => {
                    closeModal(e);
                }}
            ></div>
            <div className="upsell-modal__header">
                <div
                    className={styles.headerTopLine}
                    dangerouslySetInnerHTML={{
                        __html: cmsData.content.topline,
                    }}
                />
                <div
                    className={styles.headerHeadline}
                    dangerouslySetInnerHTML={{
                        __html: cmsData.content.headline,
                    }}
                />
                <div
                    className={styles.headerSubLine}
                    dangerouslySetInnerHTML={{
                        __html: cmsData.content.subline,
                    }}
                />
            </div>
            <div className={styles.upsellModalContent}>
                <div
                    className={styles.contentImage}
                    style={{
                        backgroundImage: `url('${cmsData.content.imgUrl}')`,
                    }}
                >
                    <div className={styles.contentImagePill}>
                        {cmsData.content.valueProps &&
                            cmsData.content.valueProps.map((prop, index) => (
                                <div
                                    key={index}
                                    className={styles.pillCopy}
                                    dangerouslySetInnerHTML={{
                                        __html: prop,
                                    }}
                                />
                            ))}
                    </div>
                </div>
                *
                <div className={styles.contentCopy}>
                    <div className={styles.contentCopyName}>
                        {cmsData.content.title}
                    </div>
                    <div className="content-copy__description">
                        {cmsData.content.description}
                        <a
                            className={concatClassNames([
                                styles.contentCopyLearnMore,
                                "al-upsell-modal__learn-more-button",
                            ])}
                            href={`${cmsData.content.productPageUrl}?pid=learnmore_modal`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t`Learn More`}
                        </a>
                    </div>
                    <div className={styles.contentCopyDetails}>
                        {suggestedProduct.attributes.option_size && (
                            <div className={styles.detailsSize}>
                                {/* TODO(i18n): Use t with interpolation to keep complete sentence together: {t`Size: ${suggestedProduct.attributes.option_size.value}`} */}
                                Size:{" "}
                                <span className="details-size__header">
                                    {
                                        suggestedProduct.attributes.option_size
                                            .value
                                    }
                                </span>
                            </div>
                        )}
                        {discountedPrice ? (
                            <div className={styles.detailsPrice}>
                                <span className={styles.priceOverride}>
                                    {format.money(discountedPrice)}
                                </span>{" "}
                                <span className={styles.priceStrike}>
                                    {format.money(
                                        suggestedProduct.price.excl_tax,
                                    )}
                                </span>
                            </div>
                        ) : (
                            <div className={styles.detailsPrice}>
                                <span className={styles.priceNormal}>
                                    {format.money(
                                        suggestedProduct.price.excl_tax,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                    <button
                        className={concatClassNames([
                            styles.contentCopyAddToCartButton,
                            "button",
                            "al-upsell-modal__add-to-cart-button",
                        ])}
                        disabled={buyIsDisabled}
                        onClick={(e) => {
                            handleAddToBasket(
                                e,
                                suggestedProduct,
                                setBuyIsDisabled,
                                props.onProceed,
                            );
                        }}
                    >
                        {t`Add to Cart`}
                    </button>
                    <a
                        className={concatClassNames([
                            styles.contentCopyNoThanks,
                            "al-upsell-modal__reject-button",
                        ])}
                        onClick={(e) => {
                            closeModal(e);
                        }}
                        dangerouslySetInnerHTML={{
                            __html: cmsData.content.rejectline,
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};
